import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';

import { LOGIN_TYPE, storageKey } from 'constants/index';
import { FLIGHT_MODULE } from 'constants/flightBooking';
import bookingReviewApi from 'api/bookingReviewApi';
import { editInformationLocateValidation } from 'utils/validate';

import PlacePublishStatusBadge from 'components/App/PlacePublishStatusBadge';
import FlightOverview from 'components/DashboardPage/Home/FlightOverview';
import Information from 'components/DashboardPage/Home/Information';
import Overview from 'components/DashboardPage/Home/Overview';
import PlaceDetail from 'components/DashboardPage/Home/PlaceDetail';
import ReviewList from 'components/DashboardPage/Home/SectionReviews';
import ModalEditLocation from 'components/Modals/ModalEditLocation';
import { landmarksOptions } from 'components/AddLocation/StepBasic/config';

import styles from './Home.module.scss';

const resolveCurrentPlace = (places, urlPlaceId) => {
  if (!places?.length) return null;

  if (urlPlaceId) {
    const matchedPlace = places.find((place) => place._id === urlPlaceId);
    if (matchedPlace) return matchedPlace;
  }

  return places[0];
};

function Home({
  myPlace,
  placeDetail,
  placeTodayBooking,
  isLoadingGetPlaceTodayBooking,
  isLoadingUpdatePlace,
  provinces,
  districts,
  wards,
  myPermission,
  actionGetMyPlace,
  actionGetPlaceDetail,
  actionGetPlaceTodayBooking,
  actionUpdatePlace,
  actionGetProvinceList,
  actionGetDistrictList,
  actionGetWardList,
}) {
  const location = useLocation();
  const [reviews, setReviews] = useState([]);
  const {
    value: isOpenModalEditLocation,
    setFalse: onCloseModalEditLocation,
    toggle: onToggleModalEditLocation,
  } = useBoolean(false);

  const isExtranet = useMemo(
    () => localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
    [],
  );

  // Intranet myPermission is modules[]; an extranet accountSetting object here is just not an
  // array, so this reads false for free (same shape utils/permission.js relies on).
  const hasFlightBookingModule = Array.isArray(myPermission)
    && myPermission.includes(FLIGHT_MODULE.BOOKING);

  const urlPlaceId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('place');
  }, [location.search]);

  const place = useMemo(() => {
    // Intranet: only ?place= (its placeDetail), never the myPlace list: ManagerBooking loads the
    // intranet places into the same store, and that list would turn "Tổng quan" into a place.
    if (!isExtranet) {
      return urlPlaceId && placeDetail?._id === urlPlaceId ? placeDetail : null;
    }

    return resolveCurrentPlace(myPlace, urlPlaceId);
  }, [isExtranet, myPlace, placeDetail, urlPlaceId]);

  const validation = useFormik({
    initialValues: {
      name: '',
      placeType: '',
      starRating: 0,
      province: '',
      market: '',
      district: '',
      ward: '',
      address: '',
      coordinates: '',
      description: '',
      landmarks: [],
    },
    validationSchema: Yup.object({
      name: editInformationLocateValidation.name,
      placeType: editInformationLocateValidation.placeType,
      starRating: editInformationLocateValidation.starRating,
      province: editInformationLocateValidation.province,
      market: editInformationLocateValidation.market,
      district: editInformationLocateValidation.district,
      ward: editInformationLocateValidation.ward,
      address: editInformationLocateValidation.address,
      coordinates: editInformationLocateValidation.coordinates,
      description: editInformationLocateValidation.description,
      landmarks: editInformationLocateValidation.landmarks,
    }),
    onSubmit: (values) => {
      if (!place?._id) return;

      actionUpdatePlace({
        placeId: place._id,
        placeInfo: {
          ...values,
          landmarks: values.landmarks?.map((item) => item.value),
        },
        callback: () => {
          onCloseModalEditLocation();
          actionGetMyPlace({});
        },
      });
    },
  });
  const { setValues } = validation;

  // Intranet: load place detail + today bookings by URL place id
  useEffect(() => {
    if (isExtranet || !urlPlaceId) return;

    actionGetPlaceDetail({ placeId: urlPlaceId });
    actionGetPlaceTodayBooking({ placeId: urlPlaceId });
  }, [
    actionGetPlaceDetail,
    actionGetPlaceTodayBooking,
    isExtranet,
    urlPlaceId,
  ]);

  // Extranet: keep existing flow based on myPlace
  useEffect(() => {
    if (!isExtranet || !place?._id) return;

    actionGetPlaceTodayBooking({
      placeId: place._id,
    });
  }, [actionGetPlaceTodayBooking, isExtranet, place?._id]);

  useEffect(() => {
    const placeId = isExtranet ? place?._id : urlPlaceId;

    const fetchReviews = async () => {
      if (!placeId) return;

      try {
        const response = await bookingReviewApi.getBookingReviewList({
          place: placeId,
          limit: 5,
          page: 1,
        });

        if (response?.payload?.data) {
          setReviews(response.payload.data);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Lỗi khi lấy danh sách đánh giá:', error);
      }
    };

    fetchReviews();
  }, [isExtranet, place?._id, urlPlaceId]);

  useEffect(() => {
    if (isExtranet || !isOpenModalEditLocation) return;

    actionGetProvinceList({});
  }, [actionGetProvinceList, isExtranet, isOpenModalEditLocation]);

  useEffect(() => {
    if (isExtranet || !isOpenModalEditLocation) return;

    if (validation.values.province && provinces.length > 0) {
      actionGetDistrictList({
        provinces: provinces.find((province) => province.name === validation.values.province)?.id,
      });
    }
  }, [
    actionGetDistrictList,
    isExtranet,
    isOpenModalEditLocation,
    provinces,
    validation.values.province,
  ]);

  useEffect(() => {
    if (isExtranet || !isOpenModalEditLocation) return;

    if (
      validation.values.district
      && validation.values.province
      && districts.length > 0
      && provinces.length > 0
    ) {
      actionGetWardList({
        provinces: provinces.find((province) => province.name === validation.values.province)?.id,
        district: districts.find((district) => district.name === validation.values.district)?.id,
      });
    }
  }, [
    actionGetWardList,
    districts,
    isExtranet,
    isOpenModalEditLocation,
    provinces,
    validation.values.district,
    validation.values.province,
  ]);

  useEffect(() => {
    if (!place) return;

    const optionLandmarksByProvince = landmarksOptions
      .find((landmark) => landmark.label === place.province);

    const landmarksSelected = place.landmarks?.map((item) => {
      const isObject = typeof item === 'object' && item !== null;
      const itemId = isObject ? (item._id || item.id) : item;
      const itemName = isObject ? (item.name || item.nameEN || '') : '';
      const optionLabel = optionLandmarksByProvince?.options
        ?.find((landmark) => landmark.value === itemId)?.label;

      return {
        value: itemId,
        label: optionLabel || itemName || itemId,
      };
    }) || [];

    setValues({
      name: place.name || '',
      placeType: place.placeType || '',
      starRating: place.starRating || 0,
      province: place.province || '',
      market: place.market?._id
        || place.market?.id
        || place.marketId
        || (typeof place.market === 'string' ? place.market : ''),
      district: place.district || '',
      ward: place.ward || '',
      address: place.address || '',
      coordinates: place.location?.coordinates?.join(', ') || '',
      description: place.description || '',
      landmarks: landmarksSelected || [],
    });
  }, [place, setValues]);

  const displayPlaceId = place ? (place.placeId || place._id) : '';
  const displayPlaceName = place?.name || '';

  return (
    <div className={styles.Component}>
      <div className="d-flex justify-content-between flex-wrap mb-24">
        <div className="TitlePage">
          <span className="TitlePage__Id">
            {place ? (
              <>
                {displayPlaceId}
                {'\u00a0-\u00a0'}
                {displayPlaceName}
              </>
            ) : 'T\u1ed5ng quan'}
          </span>

          {place && (
            <PlacePublishStatusBadge isPublished={place.isPublished} />
          )}
        </div>

        <div className="Breadcrumb">Trang chủ</div>
      </div>

      <div className="Home">
        <div className="LeftColum">
          {isExtranet && place && (
            <div className="PlaceDetailContainer">
              <PlaceDetail place={place} onEdit={onToggleModalEditLocation} />
            </div>
          )}

          {hasFlightBookingModule && <FlightOverview />}

          <div className="OverViewContainer">
            <Overview
              placeTodayBooking={placeTodayBooking}
              isLoading={isLoadingGetPlaceTodayBooking}
            />
          </div>

          <div className="ReviewListContainer">
            <ReviewList placeId={place?._id || ''} reviewList={reviews} />
          </div>
        </div>

        {place && (
          <div className="InformationContainer">
            <Information place={place} />
          </div>
        )}
      </div>

      {isOpenModalEditLocation && (
        <ModalEditLocation
          isOpenModalEditLocation={isOpenModalEditLocation}
          onToggleModal={onToggleModalEditLocation}
          validation={validation}
          provinces={provinces}
          districts={districts}
          wards={wards}
          isLoading={isLoadingUpdatePlace}
        />
      )}
    </div>
  );
}

Home.propTypes = {
  myPlace: PropTypes.arrayOf(PropTypes.shape({})),
  placeDetail: PropTypes.shape({
    _id: PropTypes.string,
  }),
  placeTodayBooking: PropTypes.shape({
    todayCheckIn: PropTypes.arrayOf(PropTypes.shape({})),
    todayCheckOut: PropTypes.arrayOf(PropTypes.shape({})),
    todayBooking: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isLoadingGetPlaceTodayBooking: PropTypes.bool,
  isLoadingUpdatePlace: PropTypes.bool,
  provinces: PropTypes.arrayOf(PropTypes.shape({})),
  districts: PropTypes.arrayOf(PropTypes.shape({})),
  wards: PropTypes.arrayOf(PropTypes.shape({})),
  // Intranet: modules[]. Extranet: an accountSetting object, never carries the flight module.
  myPermission: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  actionGetMyPlace: PropTypes.func.isRequired,
  actionGetPlaceDetail: PropTypes.func.isRequired,
  actionGetPlaceTodayBooking: PropTypes.func.isRequired,
  actionUpdatePlace: PropTypes.func.isRequired,
  actionGetProvinceList: PropTypes.func.isRequired,
  actionGetDistrictList: PropTypes.func.isRequired,
  actionGetWardList: PropTypes.func.isRequired,
};

Home.defaultProps = {
  myPlace: [],
  placeDetail: null,
  placeTodayBooking: {
    todayCheckIn: [],
    todayCheckOut: [],
    todayBooking: [],
  },
  isLoadingGetPlaceTodayBooking: false,
  isLoadingUpdatePlace: false,
  provinces: [],
  districts: [],
  wards: [],
  myPermission: [],
};

export default React.memo(Home);
